import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';

import { ResponsiveTableCard } from './ResponsiveTableCard';
import { ResponsiveTableFull } from './ResponsiveTableFull';
import { ResponsiveTableSticky } from './ResponsiveTableSticky';
import type { ResponsiveTableProps } from './types';
import { useColumnVisibility } from './useColumnVisibility';
import { useResponsiveLayout } from './useResponsiveLayout';

/**
 * Hybrid responsive table — entry point unico.
 *
 *   - < lg (< 1024px) → scroll orizzontale (nessuna colonna sticky)
 *   - ≥ lg (≥ 1024px) → table-fixed a larghezza piena
 */
export function ResponsiveTable<T>({
  data,
  columns,
  getRowKey,
  onRowClick,
  renderCardHeader,
  storageKey,
  sort,
  onSortChange,
  isLoading,
  emptyState,
  ariaLabel,
  className,
  forceLayout,
  rowClassName,
  rowClickExcludeColumnIds = ['selection'],
  fixedPageSize,
  scrollBodyOnly,
}: ResponsiveTableProps<T>) {
  const { t } = useLanguage();
  const layout = useResponsiveLayout(forceLayout);
  const visibility = useColumnVisibility(columns, storageKey);
  const columnsForFull = layout === 'full' ? visibility.visibleColumns : columns;

  if (isLoading) {
    return (
      <div className={cn('p-8 flex items-center justify-center', className)}>
          <div className="animate-pulse text-sm text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (layout === 'card') {
    return (
      <ResponsiveTableCard
        data={data}
        columns={columns}
        getRowKey={getRowKey}
        onRowClick={onRowClick}
        renderCardHeader={renderCardHeader}
        emptyState={emptyState}
        ariaLabel={ariaLabel}
        className={className}
        rowClassName={rowClassName}
        rowClickExcludeColumnIds={rowClickExcludeColumnIds}
        fixedPageSize={fixedPageSize}
      />
    );
  }

  if (layout === 'sticky') {
    return (
      <ResponsiveTableSticky
        data={data}
        columns={columns}
        getRowKey={getRowKey}
        onRowClick={onRowClick}
        sort={sort}
        onSortChange={onSortChange}
        emptyState={emptyState}
        ariaLabel={ariaLabel}
        className={className}
        rowClassName={rowClassName}
        rowClickExcludeColumnIds={rowClickExcludeColumnIds}
        fixedPageSize={fixedPageSize}
        scrollBodyOnly={scrollBodyOnly}
      />
    );
  }

  return (
    <ResponsiveTableFull
      data={data}
      columns={columnsForFull}
      getRowKey={getRowKey}
      onRowClick={onRowClick}
      sort={sort}
      onSortChange={onSortChange}
      emptyState={emptyState}
      ariaLabel={ariaLabel}
      density={visibility.density}
      rowClassName={rowClassName}
      className={className}
      rowClickExcludeColumnIds={rowClickExcludeColumnIds}
      fixedPageSize={fixedPageSize}
      scrollBodyOnly={scrollBodyOnly}
    />
  );
}
